import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bankingAccountsKeys } from "Api/Queries/Accounts/useBankingAccounts";
import { adminUserDetailKeys } from "Api/Queries/User/useAdminUserDataQuery";
import { usersAwaitingBankKeys } from "Api/Queries/User/useUsersAwaitingBankQuery";
import {
  type BankAccountStatus,
  putBankingAccountsPublicIDStatus,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  userID: number;
  onSuccess?: () => void;
};

type MutationParams = {
  bankAccountPublicID: string;
  status: BankAccountStatus;
};

export const useStatusBankingAccountMutation = ({
  onSuccess,
  userID,
}: Props) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ bankAccountPublicID, status }: MutationParams) =>
        putBankingAccountsPublicIDStatus({ status }, bankAccountPublicID),
    ),
    onError: () => {
      toast.error(t(Resources.Hooks.BankingAccount.StatusChangeError));
    },
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({
        queryKey: bankingAccountsKeys.listByUserID(userID),
      });
      queryClient.invalidateQueries({
        queryKey: adminUserDetailKeys.detailByUserID(userID),
      });
      queryClient.invalidateQueries({
        queryKey: usersAwaitingBankKeys.all,
      });
      toast.success(t(Resources.Hooks.BankingAccount.StatusChanged));
    },
  });
};
